import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPost } from "@/config/blogPosts";
import { SITE } from "@/config/site";
import { trackEvent, EVENTS } from "@/utils/track";

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mt-10 font-heading text-2xl font-bold tracking-tight text-navy">
          {block.text}
        </h2>
      );
    case "li":
      return (
        <li className="ml-5 list-disc pl-1 text-base leading-relaxed text-steel marker:text-navy-light">
          {block.text}
        </li>
      );
    case "quote":
      return (
        <blockquote className="mt-8 rounded-xl border-l-4 border-navy bg-white p-6 text-base font-medium leading-relaxed text-navy shadow-sm">
          {block.text}
        </blockquote>
      );
    default:
      return <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">{block.text}</p>;
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | ${SITE.name}`;
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute("content", post.description);
    }
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-platinum text-navy">
      <header className="sticky top-0 z-50 border-b border-[#E4E9F2] bg-white/85 backdrop-blur-xl">
        <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="post-logo" className="flex items-center">
            <img src="/logo-horizontal.png" alt={SITE.name} className="h-6 w-auto" />
          </Link>
          <Link
            to="/blog"
            data-testid="post-back-link"
            className="flex items-center gap-2 text-sm font-semibold text-navy-light hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al blog
          </Link>
        </nav>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-24">
        <article>
          <div className="flex items-center gap-3 text-xs font-medium text-steel">
            <span className="rounded-full bg-white px-3 py-1 font-semibold text-navy-light">
              {post.category}
            </span>
            <span className="flex items-center gap-1">
              <Clock3 className="h-3.5 w-3.5" />
              {post.readTime} de lectura
            </span>
          </div>
          <h1
            data-testid="post-title"
            className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl lg:leading-[1.15]"
          >
            {post.title}
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-steel">{post.description}</p>

          <div className="mt-4">
            {post.content.map((block, i) =>
              block.type === "li" ? (
                <Block key={i} block={block} />
              ) : (
                <Block key={i} block={block} />
              )
            )}
          </div>
        </article>

        <div
          data-testid="post-cta"
          className="mt-16 rounded-2xl bg-navy p-8 text-center sm:p-10"
        >
          <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">
            ¿Cuánto pierde tu clínica por los no-shows?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#C3D2E6] sm:text-base">
            Calcula tu pérdida estimada en un minuto y solicita una auditoría gratuita de
            tu agenda.
          </p>
          <Button
            asChild
            data-testid="post-cta-button"
            size="lg"
            className="mt-7 rounded-lg bg-white px-8 py-6 text-base font-semibold text-navy hover:bg-platinum"
            onClick={() => trackEvent(EVENTS.CTA_PRINCIPAL, { ubicacion: `blog_${slug}` })}
          >
            <Link to="/#calculadora">
              Calcula cuánto pierdes
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
